// src/services/announcementService.js
//
// Frontend API wrapper for runtime announcements
// ==============================================
//
// PURPOSE
// -------
// Keeps all Announcement HTTP calls in one place and hides the difference
// between:
// - protected management/internal runtime endpoints,
// - generic public read-only runtime endpoint.
//
// IMPORTANT ARCHITECTURAL RULE
// ----------------------------
// Public pages must NOT call the protected /announcements/active endpoint.
// They use /api/public/announcements/active so the public shell can render
// runtime banners without authentication and without triggering auth refresh.

import apiClient from '../utils/apiClient';

const DEV = process.env.NODE_ENV !== 'production';

function warn(label, payload) {
    if (!DEV) return;
    // eslint-disable-next-line no-console
    console.warn(label, payload || '');
}

function normalizeCreatePayload(input) {
    if (typeof input === 'string') {
        return { message: input };
    }

    return input && typeof input === 'object' ? input : {};
}

function normalizeRuntimeParams(options = {}) {
    const params = {};

    if (options.audience) params.audience = options.audience;
    if (options.surface) params.surface = options.surface;
    if (options.path) params.path = options.path;
    if (options.lang) params.lang = options.lang;
    if (options.limit) params.limit = options.limit;

    return params;
}

function ensureArray(data, label) {
    if (Array.isArray(data)) return data;
    warn(`[AnnouncementService] Unexpected response format for ${label}:`, data);
    return [];
}

/**
 * Extracts a user-facing error message from Axios/backend responses.
 *
 * Why this helper exists:
 * - the backend already returns precise validation errors such as
 *   "target_value is required for path-based announcements.";
 * - the management UI should show that exact reason instead of replacing it
 *   with a generic "Failed to create announcement" message;
 * - runtime/public consumers still fail soft and intentionally do not surface
 *   technical details to anonymous visitors.
 */
export function getAnnouncementApiErrorMessage(error, fallbackMessage) {
    const fallback = fallbackMessage || 'Announcement operation failed.';

    const data = error?.response?.data;

    if (typeof data === 'string' && data.trim()) {
        return data.trim();
    }

    if (data && typeof data === 'object') {
        const candidates = [
            data.message,
            data.error,
            data.details,
        ];

        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim();
            }
        }
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
        return error.message.trim();
    }

    return fallback;
}

// -----------------------------------------------------------------------------
// Protected management API
// -----------------------------------------------------------------------------

export const createAnnouncement = async (payloadOrMessage) => {
    try {
        const payload = normalizeCreatePayload(payloadOrMessage);
        const response = await apiClient.post('/announcements/create', payload, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error('[AnnouncementService] Creating announcement failed:', error);
        throw error;
    }
};

export const updateAnnouncement = async (announcementId, payload) => {
    try {
        const response = await apiClient.put(`/announcements/${announcementId}`, payload || {}, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error(`[AnnouncementService] Updating announcement (ID: ${announcementId}) failed:`, error);
        throw error;
    }
};

export const startAnnouncement = async (id) => {
    try {
        const response = await apiClient.put(`/announcements/start/${id}`, {}, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error(`[AnnouncementService] Starting announcement (ID: ${id}) failed:`, error);
        throw error;
    }
};

export const stopAnnouncement = async (id) => {
    try {
        const response = await apiClient.put(`/announcements/stop/${id}`, {}, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error(`[AnnouncementService] Stopping announcement (ID: ${id}) failed:`, error);
        throw error;
    }
};

export const fetchAllAnnouncements = async () => {
    try {
        const response = await apiClient.get('/announcements/all', {
            withCredentials: true,
        });
        return ensureArray(response.data, 'fetchAllAnnouncements');
    } catch (error) {
        console.error('[AnnouncementService] Fetching all announcements failed:', error);
        throw error;
    }
};

export const deleteAnnouncement = async (announcementId) => {
    try {
        const response = await apiClient.delete(`/announcements/${announcementId}`, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error('[AnnouncementService] Deleting announcement failed:', error);
        throw error;
    }
};

// -----------------------------------------------------------------------------
// Runtime read API
// -----------------------------------------------------------------------------

export const fetchActiveAnnouncements = async (options = {}) => {
    try {
        const response = await apiClient.get('/announcements/active', {
            withCredentials: true,
            params: normalizeRuntimeParams(options),
        });

        return ensureArray(response.data, 'fetchActiveAnnouncements');
    } catch (error) {
        console.error('[AnnouncementService] Failed to fetch internal runtime announcements:', error);
        return [];
    }
};

export const fetchPublicActiveAnnouncements = async (options = {}) => {
    try {
        const response = await apiClient.get('/api/public/announcements/active', {
            // Public endpoint. Keeping credentials does not hurt and stays aligned
            // with the existing app-wide apiClient usage, but the backend must not
            // require a session here.
            withCredentials: true,
            params: normalizeRuntimeParams(options),
        });

        return ensureArray(response.data, 'fetchPublicActiveAnnouncements');
    } catch (error) {
        // Public shell must fail soft: if announcements cannot be loaded, /shop
        // must continue to sell vouchers normally.
        console.error('[AnnouncementService] Failed to fetch public runtime announcements:', error);
        return [];
    }
};
